import { useEffect, useCallback, useState, useRef } from 'react';
import { useRefreshContext } from '../contexts/RefreshContext';

interface AutoRefreshState {
  lastUpdated: Date | null;
  isRefreshing: boolean;
  secondsSinceUpdate: number;
  isPaused: boolean;
  pauseReason: string | null;
}

interface UseAutoRefreshOptions {
  intervalMs?: number;
  enabled?: boolean;
  respectUserActivity?: boolean; // If true, uses smart refresh (default: true)
  onRefreshStart?: () => void;
  onRefreshEnd?: () => void;
}

/**
 * Hook for auto-refreshing data at regular intervals with smart activity detection.
 *
 * By default, respects user activity and won't refresh when:
 * - A modal is open
 * - User has unsaved changes
 * - User is typing in an input
 * - User has been active in the last 10 seconds
 *
 * @param fetchFunction - The function to call to refresh data
 * @param options - Configuration options
 * @returns State object with refresh status and pause information
 *
 * @example
 * const { isRefreshing, isPaused, secondsSinceUpdate } = useAutoRefresh(
 *   refetchData,
 *   { intervalMs: 30000 }
 * );
 */
export function useAutoRefresh(
  fetchFunction: () => void | Promise<void>,
  options: UseAutoRefreshOptions = {}
): AutoRefreshState {
  const {
    intervalMs = 30000, // 30 seconds default
    enabled = true,
    respectUserActivity = true,
    onRefreshStart,
    onRefreshEnd,
  } = options;

  const [lastUpdated, setLastUpdated] = useState<Date | null>(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [secondsSinceUpdate, setSecondsSinceUpdate] = useState(0);
  const fetchRef = useRef(fetchFunction);

  // Get smart refresh context
  const { canAutoRefresh, isPaused, pauseReason } = useRefreshContext();

  // Determine if refresh is actually allowed
  const canRefresh = enabled && (!respectUserActivity || canAutoRefresh);

  // Keep fetch function ref updated
  useEffect(() => {
    fetchRef.current = fetchFunction;
  }, [fetchFunction]);

  // Execute refresh
  const doRefresh = useCallback(async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    onRefreshStart?.();

    try {
      await fetchRef.current();
      setLastUpdated(new Date());
    } catch (error) {
      console.error('[AutoRefresh] Error during refresh:', error);
    } finally {
      setIsRefreshing(false);
      onRefreshEnd?.();
    }
  }, [isRefreshing, onRefreshStart, onRefreshEnd]);

  // Auto-refresh interval - only triggers when canRefresh is true
  useEffect(() => {
    if (!enabled) return;

    const interval = setInterval(() => {
      // Check canAutoRefresh at execution time (not just setup time)
      if (canRefresh) {
        doRefresh();
      }
    }, intervalMs);

    return () => clearInterval(interval);
  }, [enabled, intervalMs, doRefresh, canRefresh]);

  // Update seconds counter
  useEffect(() => {
    const ticker = setInterval(() => {
      if (lastUpdated) {
        const seconds = Math.floor((Date.now() - lastUpdated.getTime()) / 1000);
        setSecondsSinceUpdate(seconds);
      }
    }, 1000);

    return () => clearInterval(ticker);
  }, [lastUpdated]);

  return {
    lastUpdated,
    isRefreshing,
    secondsSinceUpdate,
    isPaused: respectUserActivity ? isPaused : false,
    pauseReason: respectUserActivity ? pauseReason : null,
  };
}

/**
 * Format seconds since update into human-readable string
 */
export function formatTimeSince(seconds: number): string {
  if (seconds < 5) return 'Just now';
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  return `${Math.floor(seconds / 3600)}h ago`;
}

export default useAutoRefresh;
